/**
 * LibraryAssetDrawer - Slide-in drawer for selecting images from the library
 * Opens over the reference pool area in the LoRA wizard
 *
 * Uses shared AssetDrawerContent for consistent presentation across the app.
 */

import { useState, useMemo, useCallback } from "react";
import { ImageIcon, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useAssets } from "@/hooks/useAssets";
import { getAssetType } from "@/types/asset.types";
import { LazyImage } from "@/components/shared/LazyImage";
import { AssetDrawerContent } from "@/components/shared/asset-drawer";
import "@/components/shared/asset-drawer/AssetDrawer.css";

interface LibraryAssetDrawerProps {
  /** Project ID to load assets from */
  projectId: string | undefined;
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Called when drawer should close */
  onClose: () => void;
  /** Called when assets are selected and confirmed */
  onAddAssets: (assets: SelectedAsset[]) => void;
  /** Asset IDs that are already in the reference pool (to filter out) */
  existingAssetIds?: Set<string>;
}

export interface SelectedAsset {
  id: string;
  url: string;
  thumbnailUrl?: string;
  filename?: string;
}

export function LibraryAssetDrawer({
  projectId,
  isOpen,
  onClose,
  onAddAssets,
  existingAssetIds = new Set(),
}: LibraryAssetDrawerProps) {
  const { t } = useTranslation();
  const { assets, isLoading } = useAssets(projectId, { fetchEnabled: isOpen });
  const [searchValue, setSearchValue] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [hideExisting, setHideExisting] = useState(true);

  // Filter to only images, apply search, and optionally hide existing
  const filteredAssets = useMemo(() => {
    let filtered = assets.filter((asset) => {
      const assetType = getAssetType(asset.mimeType);
      return assetType === "image";
    });

    // Hide assets already in reference pool
    if (hideExisting) {
      filtered = filtered.filter((a) => !existingAssetIds.has(a.id));
    }

    // Apply search filter
    if (searchValue) {
      const search = searchValue.toLowerCase();
      filtered = filtered.filter((a) =>
        a.filename?.toLowerCase().includes(search),
      );
    }

    // Sort by newest first
    return [...filtered].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [assets, searchValue, hideExisting, existingAssetIds]);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set(filteredAssets.map((a) => a.id)));
  }, [filteredAssets]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleAdd = useCallback(() => {
    const selectedAssets: SelectedAsset[] = assets
      .filter((a) => selectedIds.has(a.id))
      .map((a) => ({
        id: a.id,
        url: a.thumbnailUrl || "",
        thumbnailUrl: a.thumbnailUrl,
        filename: a.filename,
      }));
    onAddAssets(selectedAssets);
    setSelectedIds(new Set());
    onClose();
  }, [assets, selectedIds, onAddAssets, onClose]);

  const handleClose = useCallback(() => {
    setSelectedIds(new Set());
    setSearchValue("");
    onClose();
  }, [onClose]);

  // Render bulk actions (select all / clear selection)
  const renderBulkActions = useCallback(() => {
    if (filteredAssets.length === 0) return null;
    return (
      <>
        <button
          type="button"
          className="asset-drawer__bulk-action"
          onClick={handleSelectAll}
        >
          {t("assetDrawer.selectAll", "Select all")}
        </button>
        {selectedIds.size > 0 && (
          <>
            <span className="asset-drawer__bulk-separator">·</span>
            <button
              type="button"
              className="asset-drawer__bulk-action"
              onClick={handleClearSelection}
            >
              {t("assetDrawer.clearSelection", "Clear selection")}
            </button>
          </>
        )}
      </>
    );
  }, [
    filteredAssets.length,
    selectedIds.size,
    handleSelectAll,
    handleClearSelection,
    t,
  ]);

  // Dynamic button label based on selection count
  const getAddButtonLabel = useCallback(
    (count: number) => {
      if (count === 0) {
        return t("loraWizard.addReferences", "Add References");
      }
      return t("loraWizard.addNReferences", "Add {{count}} Reference", {
        count,
        defaultValue_one: "Add {{count}} Reference",
        defaultValue_other: "Add {{count}} References",
      });
    },
    [t],
  );

  // Get empty state message
  const getEmptyMessage = useCallback(() => {
    if (assets.length === 0) {
      return t("loraWizard.noImagesInProject", "No images in this project");
    }
    if (hideExisting) {
      return t("loraWizard.allImagesAlreadyAdded", "All images already added");
    }
    return t("loraWizard.noImagesFound", "No images found");
  }, [assets.length, hideExisting, t]);

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        "absolute inset-0 bg-[#111] z-10 flex flex-col",
        "animate-in slide-in-from-right duration-200",
      )}
    >
      <AssetDrawerContent
        title={t("loraWizard.selectFromLibrary", "Select from Library")}
        description={t(
          "loraWizard.imagesAvailable",
          "{{count}} images available",
          {
            count: filteredAssets.length,
            defaultValue_one: "{{count}} image available",
            defaultValue_other: "{{count}} images available",
          },
        )}
        headerLayout="inline"
        onClose={handleClose}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder={t("loraWizard.searchImages", "Search images...")}
        hideExisting={hideExisting}
        onHideExistingChange={setHideExisting}
        hideExistingLabel={t(
          "assetDrawer.hideAlreadyAdded",
          "Hide already added",
        )}
        renderBulkActions={renderBulkActions}
        selectedCount={selectedIds.size}
        onAdd={handleAdd}
        addButtonLabel={getAddButtonLabel}
        showAddButtonPrefix={false}
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-32 text-sm text-[var(--color-primitive-neutral-0)]/40">
            {t("loraWizard.loadingImages", "Loading images...")}
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-sm text-[var(--color-primitive-neutral-0)]/40">
            {getEmptyMessage()}
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-3">
            {filteredAssets.map((asset) => (
              <LibraryAssetCard
                key={asset.id}
                thumbnailUrl={asset.thumbnailUrl}
                filename={asset.filename}
                selected={selectedIds.has(asset.id)}
                onToggle={() => handleToggleSelect(asset.id)}
              />
            ))}
          </div>
        )}
      </AssetDrawerContent>
    </div>
  );
}

/** Card component for library asset grid */
interface LibraryAssetCardProps {
  thumbnailUrl?: string;
  filename?: string;
  selected: boolean;
  onToggle: () => void;
}

function LibraryAssetCard({
  thumbnailUrl,
  filename,
  selected,
  onToggle,
}: LibraryAssetCardProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "relative aspect-square overflow-hidden border-2 transition-all rounded-[8px]",
        selected
          ? "border-primary ring-2 ring-primary/30"
          : "border-transparent hover:border-white/20",
      )}
    >
      {thumbnailUrl ? (
        <LazyImage
          src={thumbnailUrl}
          alt={filename || "Asset"}
          className="w-full h-full object-cover rounded-[6px]"
        />
      ) : (
        <div className="w-full h-full bg-white/5 flex items-center justify-center rounded-[6px]">
          <ImageIcon className="w-8 h-8 text-white/20" />
        </div>
      )}
      {/* Selection indicator */}
      <div
        className={cn(
          "absolute top-2 right-2 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
          selected
            ? "bg-primary border-primary"
            : "bg-black/50 border-white/30",
        )}
      >
        {selected && <Check className="w-3 h-3 text-white" />}
      </div>
    </button>
  );
}
